import {useMutation} from '@apollo/client'
import gql from 'graphql-tag'
import {useEffect, useState} from 'react'
import {useRedirect} from 'react-admin'

import {
  PushNotification,
  PushNotificationsProvider,
} from '../contexts/PushNotificationContext'
import useBrowserNotifications from '../hooks/useBrowserNotifications'
import useSessionMerchantUser from '../hooks/useSessionMerchantUser'
import {MutationRoot} from '../types/graphqlSchema'
import {isNativeMobileApp, platform} from '../utils/platform'

const PushNotificationsHandler = ({children}) => {
  const {showNotification} = useBrowserNotifications()
  const [deviceToken, setDeviceToken] = useState<any>()
  const [pushNotification, setPushNotification] = useState<PushNotificationSource>()
  const {merchantUser} = useSessionMerchantUser()
  const [
    upsertDeviceToken,
  ] = useMutation<MutationRoot['insert_merchant_app_installations_one']>(
    UPSERT_DEVICE_TOKEN_MUTATION
  )
  const redirect = useRedirect()
  useEffect(() => {
    if (!(deviceToken && merchantUser)) return
    upsertDeviceToken({variables: {
      deviceToken,
      merchantUserId: merchantUser.id,
      platform: platform.toUpperCase(),
    }})
  }, [deviceToken, merchantUser, upsertDeviceToken])
  useEffect(() => {
    if (!pushNotification) return
    const linkPath = pushNotification.payload.data.linkPath
    switch (pushNotification.source) {
      case "BACKGROUND": {
        if (pushNotification.backgroundAction === 'NOTIFICATIONCLICK') {
          if (isNativeMobileApp) window.location.href = linkPath ?? ''
          else redirect(linkPath)
        }
        break
      }
      case "FOREGROUND": {
        if (isNativeMobileApp) return
        const notification = JSON.parse(pushNotification.payload.data.notification)
        // TODO: in the future, for new message notification, do not show the
        //  notification if the user is already in the
        //  chat (window.location.href.endsWith(linkPath)).
        //  Instead, if the user is in the chat already, show an indicator in the chat
        //  page so that the user is aware that an unread message has arrived in the
        //  chat(just like whatsapp does with the arrow at the bottom right of the chat).
        showNotification({
          body: notification.body,
          onclick: e => {
            e.target.close()
            redirect(linkPath)
          },
          title: notification.title,
        })
        break
      }
    }
    setPushNotification(undefined)
  }, [pushNotification, redirect, showNotification])
  return (
    <PushNotificationsProvider
      disabled={!merchantUser}
      onBackgroundPushNotificationActionPerformed={(action, payload) =>
        setPushNotification({
          backgroundAction: action, payload, source: 'BACKGROUND',
        })
      }
      onForegroundPushNotificationReceived={payload =>
        setPushNotification({payload, source: 'FOREGROUND'})
      }
      onReceiveDeviceToken={setDeviceToken}
    >
      {children}
    </PushNotificationsProvider>
  )
}


// Updates `merchant_user_id` on device_identifier upsert conflict which occurs when the
// same flinkit app is used by different merchant users
const UPSERT_DEVICE_TOKEN_MUTATION = gql`
  mutation (
    $deviceToken: String!
    $merchantUserId: uuid!
    $platform: app_platforms_enum!
  ) {
    insert_merchant_app_installations_one(
      object: {
        pushNotificationsDeviceToken: $deviceToken
        merchantUserId: $merchantUserId
        platform: $platform
      }
      on_conflict: {
        constraint: merchant_app_installations_push_notifications_device_token_key
        update_columns: [merchantUserId]
      }
    ) {id}
  }
`

interface PushNotificationSource {
  backgroundAction?: string
  payload: PushNotification
  source: 'FOREGROUND' | 'BACKGROUND'
}

export default PushNotificationsHandler
